/**
 * Hand the current tab over to an external URL (Stripe Checkout, Billing
 * Portal, OAuth consent screens...).
 *
 * Deliberately NOT `window.open(url, '_blank')`. These URLs are only known
 * after an `await fetch(...)`, and that await consumes the user-activation
 * window created by the tap. WebKit — i.e. every browser on iOS, including
 * Chrome and Firefox — then blocks the popup and `window.open` returns `null`,
 * so the user sees absolutely nothing happen. Desktop Chrome/Safari apply a
 * looser async-popup heuristic, which is why this only ever broke on mobile.
 *
 * Same-tab navigation carries no such restriction and is what Stripe
 * recommends for mobile checkout; the session's `success_url` / `cancel_url`
 * bring the user straight back into the app.
 *
 * Returns `false` when the URL is missing/unusable or the navigation threw, so
 * callers can surface an error instead of failing silently.
 */
export function redirectToExternalUrl(url: unknown): boolean {
  if (typeof url !== "string" || url.trim() === "") return false;

  let parsed: URL;
  try {
    parsed = new URL(url, window.location.href);
  } catch {
    return false;
  }

  // Never hand the address bar to javascript:, data: or blob: URLs.
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return false;

  try {
    window.location.assign(parsed.href);
    return true;
  } catch {
    return false;
  }
}
